import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  VStack,
  FormControl,
  FormErrorMessage,
  Button,
  HStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, ArrowBackIcon } from "@chakra-ui/icons";
import { ThemedFormLabel } from "../../../homeowners/hire/themed-form-label";
import { ThemedInput } from "../../../homeowners/hire/themed-input";
import { ContactDetailsFormData, contactDetailsSchema } from "../../schemas/schema";

interface ContactDetailsStepProps {
  onStepComplete: (data: ContactDetailsFormData) => void;
  onPrevious: () => void;
  isLoading?: boolean;
  defaultValues?: Partial<ContactDetailsFormData>;
}

export const ContactDetailsStep: React.FC<ContactDetailsStepProps> = ({
  onStepComplete,
  onPrevious,
  isLoading = false,
  defaultValues,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ContactDetailsFormData>({
    resolver: zodResolver(contactDetailsSchema),
    defaultValues,
  });

  const onSubmit = (data: ContactDetailsFormData) => onStepComplete(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={6} align="stretch">
        <FormControl isInvalid={!!errors.firstName}>
          <ThemedFormLabel>First Name</ThemedFormLabel>
          <ThemedInput
            {...register("firstName")}
            placeholder="Enter your first name"
          />
          <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.lastName}>
          <ThemedFormLabel>Last Name</ThemedFormLabel>
          <ThemedInput
            {...register("lastName")}
            placeholder="Enter your last name"
          />
          <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.email}>
          <ThemedFormLabel>Email Address</ThemedFormLabel>
          <ThemedInput
            {...register("email")}
            type="email"
            placeholder="Enter your email address"
          />
          <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.phone}>
          <ThemedFormLabel>Phone Number</ThemedFormLabel>
          <ThemedInput
            {...register("phone")}
            type="tel"
            placeholder="Enter your phone number"
          />
          <FormErrorMessage>{errors.phone?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing={4} mt={4}>
          <Button
            type="button"
            onClick={onPrevious}
            leftIcon={<ArrowBackIcon />}
            size="lg"
            flex="1"
            variant="outline"
            isDisabled={isLoading}
          >
            Previous
          </Button>
          <Button
            type="submit"
            size="lg"
            flex="1"
            colorScheme="flatteredFlamingo"
            rightIcon={<ArrowForwardIcon />}
            isLoading={isLoading}
            loadingText="Processing..."
          >
            Next
          </Button>
        </HStack>
      </VStack>
    </form>
  );
};


